import { Controller } from "@hotwired/stimulus"
import TomSelect from "tom-select"

// Controller genérico para selects con búsqueda remota (Tom Select).
// Soporta single select y multiselect con tags.
//
// Uso single select:
//   select data-controller="manage--tom-select"
//          data-manage--tom-select-url-value="/search.json"
//
// Uso multiselect:
//   select multiple data-controller="manage--tom-select"
//          data-manage--tom-select-url-value="/search.json"
//
// El endpoint debe retornar JSON: [{ id: 1, name: "Texto" }, ...]
export default class extends Controller {
  static values = {
    url: String,
    labelField: { type: String, default: "name" },
    valueField: { type: String, default: "id" },
    searchField: { type: String, default: "name" },
    preload: { type: Boolean, default: true },
    placeholder: { type: String, default: "Seleccionar..." },
    create: { type: Boolean, default: false },
    minChars: { type: Number, default: 0 },
    // Cuando es true, cada option/item renderiza el HTML crudo de su
    // atributo data-badge (ej: un badge de estado) junto al label.
    // El HTML lo genera el server, así respeta el namespace (admin/manage).
    badges: { type: Boolean, default: false }
  }

  connect() {
    if (this.element.tomselect) return

    this.select = new TomSelect(this.element, this.#config())
  }

  disconnect() {
    if (this.select) {
      this.select.destroy()
      this.select = null
    }
  }

  // --- Private ---

  #config() {
    const isMultiple = this.element.multiple
    const config = {
      plugins: isMultiple ? { remove_button: { title: "Quitar" } } : { clear_button: { title: "Quitar" } },
      placeholder: this.placeholderValue,
      create: this.createValue,
      hideSelected: true,
      closeAfterSelect: true
    }

    if (this.badgesValue) {
      config.render = this.#badgeRenderers()
    }

    if (this.hasUrlValue) {
      Object.assign(config, {
        valueField: this.valueFieldValue,
        labelField: this.labelFieldValue,
        searchField: [this.searchFieldValue],
        loadThrottle: 300,
        preload: this.preloadValue,
        dropdownParent: "body",
        load: (query, callback) => {
          if (query.length < this.minCharsValue) return callback()

          fetch(`${this.urlValue}?q=${encodeURIComponent(query)}`, {
            headers: { "Accept": "application/json" }
          })
            .then(r => r.json())
            .then(callback)
            .catch(() => callback())
        }
      })
      config.render = Object.assign({}, config.render, {
        no_results: () => '<div class="ts-no-results">Sin resultados</div>'
      })
    }

    return config
  }

  // Renderiza el label (texto) escapado + el HTML crudo del badge (data-badge).
  #badgeRenderers() {
    const label = (data) => data.text ?? data[this.labelFieldValue] ?? ""
    const badge = (data) => (data.badge ? ` ${data.badge}` : "")

    return {
      option: (data, escape) =>
        `<div class="flex items-center gap-2 flex-wrap">${escape(label(data))}${badge(data)}</div>`,
      item: (data, escape) =>
        `<div class="inline-flex items-center gap-1.5">${escape(label(data))}${badge(data)}</div>`
    }
  }
}
